// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import {
  QueryBuilder,
  BaseQueryBuilderPayload,
  Tag,
  StyledModal,
  Button,
  TitleMedium,
  COLORS,
  BASE_COLORS,
  DropdownPopover,
  TitleSmall,
  BodySecondary,
} from "@bikdotai/bik-component-library";
import {
  getFilters,
  getEventsName,
  getTicketFormFieldMap,
} from "../../services/helpers/QueryBuilderHelper";
import {
  BuilderContainer,
  InstaDropdownContainer,
  InstaDropdownWrapper,
} from "./styled";
import { QueryValidator } from "./queryValidator";
import { extractHeaderAndSubHeader } from "./utils";
import { populateChannelIdsDropdownForFlow } from "../../services/helpers/domainScoping";
import { CHANNELS } from "../../utilities/login.utility";
import { WarnIcon } from "../../icons/warnIcon";
import { FilterActions } from "../../app/action-block/filter/models";
import {
  customValuesQuery,
  generateDummyQueryBuilderResponse,
  GMAIL_LABELS,
  SpecialTrigger,
  FormSubmittedProperty,
} from "../../constants/customQueryBuilderValues";
import { Body, Container, Footer, Header } from "../ig-modals/style";
import { v4 as uuidv4 } from "uuid";
import WhatsappIcon from "../../icons/whatsappIcon";
import InstagramIcon from "../../icons/instagramIcon";
import ChevronDownIcon from "../../icons/chevron_down";
import { useAppSelector } from "../../state/store";
import EmailIcon from "../../icons/email";
import {
  prepareBoolean,
  reverseParseBoolean,
} from "../../app/action-block/filter/utils";
import { useVersionJet } from "../../utilities/useVersionJet";
import {
  formFieldCache,
  getFormProperties,
  getFormFieldValues,
} from "./formSubmittedHelper";

const handleCustomValues = (key: string) => {
  switch (key) {
    case "gmailReceived_labels":
      return generateDummyQueryBuilderResponse(GMAIL_LABELS);
    default:
      return generateDummyQueryBuilderResponse([]);
  }
};

const queryValidator = new QueryValidator();
let builderState = {};
let variableList: any[] = [];
// Stores the initial nodes passed as props so eventsTriggerApiHandler can
// read them even when the QB library caches its typesAPIHandlers closure from
// an earlier render where props.nodes was still empty.
let initialNodesCache: any[] = [];
// When handleStateChange explicitly overrides initialNodesCache (on form clear
// or form change), set this to true so the render-time assignment doesn't
// overwrite it on the next re-render triggered by setQbKey.
let initialNodesCacheManuallySet = false;

export const eventsTriggerApiHandler = async (
  payload: BaseQueryBuilderPayload<
    | undefined
    | { triggerName: string; propertyName: string; searchKey?: string }
  >,
  storeId: string,
  channel: string,
  category?: string,
  isStoreEnabledForChatSessionAutoClose?: boolean,
  onVariableListUpdate?: (list: string[]) => void
) => {
  if (!payload.query) {
    const response = await getEventsName(
      {
        storeId,
        token: storeId,
        action: FilterActions.GET_EVENT_NAMES,
        channel,
        category,
      },
      channel
    );
    if (response.data) {
      let filteredData = { ...response.data.data };
      let needsFilter = false;

      // Filter out chat session auto close events if store doesn't have access
      if (!isStoreEnabledForChatSessionAutoClose && filteredData) {
        needsFilter = true;
        Object.keys(filteredData).forEach((key) => {
          if (Array.isArray(filteredData[key])) {
            filteredData[key] = filteredData[key].filter(
              (event: any) => event.id !== "chatSessionAutoClosed"
            );
          }
        });
      }

      // Filter out formSubmitted event for non-email channels
      if (channel !== CHANNELS.EMAIL && filteredData) {
        needsFilter = true;
        Object.keys(filteredData).forEach((key) => {
          if (Array.isArray(filteredData[key])) {
            filteredData[key] = filteredData[key].filter(
              (event: any) => event.id !== SpecialTrigger.FormSubmitted
            );
          }
        });
      }

      // Category-level filtering (email channel only):
      // "EmailReceived" (virtual) → only inbound email events from Inbox
      // any other category key  → that category only, with inbound email events excluded from Inbox
      if (category && channel === CHANNELS.EMAIL) {
        const INBOUND_EMAIL_IDS = new Set(["emailReceived", "gmailReceived"]);
        needsFilter = true;
        if (category === "EmailReceived") {
          const inboxEvents = (filteredData["Inbox"] ?? []) as any[];
          filteredData = {
            Inbox: inboxEvents.filter((e: any) => INBOUND_EMAIL_IDS.has(e.id)),
          };
        } else if (filteredData[category]) {
          const categoryEvents = filteredData[category] as any[];
          filteredData = {
            [category]:
              category === "Inbox"
                ? categoryEvents.filter((e: any) => !INBOUND_EMAIL_IDS.has(e.id))
                : categoryEvents,
          };
        }
      }

      if (needsFilter) {
        return { ...response.data, data: filteredData };
      }
      return response.data;
    }
  }
  if (payload.query?.triggerName && !payload.query?.propertyName) {
    if (payload.query.triggerName === SpecialTrigger.WhatsAppCart) {
      return {
        data: [],
        status: 200,
      };
    }

    if (payload.query.triggerName === SpecialTrigger.FormSubmitted) {
      const builderStateProps = (builderState as any)?.nodes?.[0]?.properties;
      // Only treat builderStateProps as authoritative when it actually contains
      // a form prop with a value. QB fires onStateChange with an empty array
      // during initial edit-mode hydration before it populates from props.nodes,
      // so an empty builderStateProps is not reliable — fall back to initialNodesCache.
      const existingProperties: any[] =
        (builderStateProps?.some(
          (p: any) =>
            (p.name === FormSubmittedProperty.FormId ||
              p.name === FormSubmittedProperty.FormName) &&
            p.value
        )
          ? builderStateProps
          : null) ??
        initialNodesCache?.[0]?.properties ??
        [];
      const result = await getFormProperties(storeId, existingProperties);
      if (result.variableList) {
        if (onVariableListUpdate) onVariableListUpdate(result.variableList);
        else variableList = result.variableList;
      }
      return { data: result.data, status: result.status };
    }

    const response = await getFilters(
      {
        storeId,
        token: storeId,
        action: FilterActions.GET_EVENT_PROPERTY_NAMES,
        eventId: payload.query.triggerName,
      },
      channel
    );

    if (response.data) {
      const newList = response.data.map((item) => item.id);
      if (onVariableListUpdate) onVariableListUpdate(newList);
      else variableList = newList;
      return response;
    }
  }
  if (
    payload.query?.triggerName &&
    payload.query?.propertyName &&
    !payload.query?.searchKey
  ) {
    if (payload.query.triggerName === SpecialTrigger.FormSubmitted) {
      return getFormFieldValues(storeId, payload.query.propertyName);
    }

    const customValueNeeded = customValuesQuery.some((obj) => {
      return (
        obj.triggerName === payload.query?.triggerName &&
        obj.propertyName === payload.query?.propertyName
      );
    });
    if (customValueNeeded) {
      return handleCustomValues(
        `${payload.query?.triggerName}_${payload.query?.propertyName}`
      );
    }
    const response = await getFilters(
      {
        storeId,
        token: storeId,
        action: FilterActions.GET_EVENT_PROPERTY_VALUES,
        eventId: payload.query.triggerName,
        property: payload.query.propertyName,
      },
      channel
    );
    if (response.data) {
      return response;
    }
  }
  if (
    payload.query?.triggerName &&
    payload.query?.propertyName &&
    payload.query?.searchKey
  ) {
    if (payload.query.triggerName === SpecialTrigger.FormSubmitted) {
      return getFormFieldValues(
        storeId,
        payload.query.propertyName,
        payload.query.searchKey
      );
    }

    const customValueNeeded = customValuesQuery.some((obj) => {
      return (
        obj.triggerName === payload.query?.triggerName &&
        obj.propertyName === payload.query?.propertyName
      );
    });
    if (customValueNeeded) {
      return handleCustomValues(
        `${payload.query?.triggerName}_${payload.query?.propertyName}`
      );
    }
    const response = await getFilters(
      {
        storeId,
        token: storeId,
        action: FilterActions.SEARCH_EVENT_PROPERTY_VALUES,
        eventId: payload.query.triggerName,
        property: payload.query?.propertyName,
        searchText: payload.query?.searchKey,
      },
      channel
    );
    if (response.data) {
      return response;
    }
  }
  return { status: 200 };
};

const EventsBuilder: React.FC<{
  visible: boolean;
  onClose: Function;
  onSave: Function;
  nodes: [];
  mode: string;
  storeId: string;
  channel: string;
  currentChannelId?: string;
  category?: string;
  ctaText?: string;
  onBack?: () => void;
  triggerId?: string;
  footerCopy?: string;
}> = (props) => {
  const [errorState, setErrState] = useState(undefined);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [channelIdOptions, setChannelIdOptions] = useState<
    {
      label: string;
      value: string;
      selected: boolean;
    }[]
  >([]);
  const [selectedChannel, setSelectedChannel] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [qbKey, setQbKey] = useState(0);
  const previousFormIdRef = useRef<string | null>(null);
  const qbResetStateRef = useRef<any>(undefined);
  // True once the user has explicitly confirmed a form ID at least once.
  // Allows reset when the same form ID is re-selected after deletion,
  // while never resetting on the initial edit-mode render.
  const wasFormIdEverSetRef = useRef(false);
  const { isVersionApplicabletoPlan } = useVersionJet();
  const channels: { [key: string]: any } = useAppSelector(
    (state) => state.flowMetaState.channels!
  );
  const domainChannelMap = useAppSelector((state) => state.flowMetaState.domainChannelMap);
  const flowMetaState: { [key: string]: any } = useAppSelector((state) => state.flowMetaState.flowMeta);
  const currentFlowId = useAppSelector((state) => state.homeState.flowId);
  const domain = flowMetaState[currentFlowId]?.domain;
  const channelSettingsId = Object.keys(flowMetaState[currentFlowId]?.channelSettings ?? {})[0] ?? "";

  // Synchronously update so API handler always sees current nodes,
  // even if the QB component has cached its typesAPIHandlers closure.
  // Skip if handleStateChange has explicitly set the cache (form clear/change).
  if (!initialNodesCacheManuallySet) {
    initialNodesCache = props.nodes ?? [];
  }

  const eventQbRef = useRef();

  const handleModalClose = () => {
    builderState = {};
    initialNodesCache = [];
    initialNodesCacheManuallySet = false;
    previousFormIdRef.current = null;
    qbResetStateRef.current = undefined;
    wasFormIdEverSetRef.current = false;
    setErrState(undefined);
    if (eventQbRef && eventQbRef.current) {
      eventQbRef.current.clearAll("EVENTS_TRIGGER");
    }
    props.onClose();
  };

  const handleStateChange = (state: any) => {
    builderState = state;
    const metas: any[] = state?.nodes?.[0]?.metas ?? [];
    const isFormSubmitted = metas?.[0]?.value === SpecialTrigger.FormSubmitted;
    if (!isFormSubmitted) return;

    const properties: any[] = state?.nodes?.[0]?.properties ?? [];

    // Strip duplicate form-identifier conditions immediately. Keep only the
    // first one that has a value; if none have a value yet, keep the first.
    const formIdentifiers = properties.filter(
      (p: any) =>
        p.name === FormSubmittedProperty.FormId ||
        p.name === FormSubmittedProperty.FormName
    );
    if (formIdentifiers.length > 1) {
      const toKeep = formIdentifiers.find((p: any) => p.value) ?? formIdentifiers[0];
      const deduped = properties.filter(
        (p: any) =>
          (p.name !== FormSubmittedProperty.FormId &&
            p.name !== FormSubmittedProperty.FormName) ||
          p._id === toKeep._id
      );
      const dedupedNode = { ...state.nodes[0], properties: deduped };
      const dedupedState = { ...state, nodes: [dedupedNode] };
      builderState = dedupedState;
      qbResetStateRef.current = dedupedState;
      initialNodesCache = [dedupedNode];
      initialNodesCacheManuallySet = true;
      setQbKey((k) => k + 1);
      return;
    }

    const formProp = properties.find(
      (p: any) =>
        (p.name === FormSubmittedProperty.FormId ||
          p.name === FormSubmittedProperty.FormName) &&
        p.value
    );
    const rawValue = formProp?.value;
    const newFormId = Array.isArray(rawValue)
      ? (rawValue[0]?.id ?? rawValue[0])
      : typeof rawValue === "object"
      ? rawValue?.id
      : rawValue;

    // Form ID cleared
    // property dropdowns and strip any dangling field conditions.
    if (!newFormId) {
      if (previousFormIdRef.current !== null) {
        const cleanProperties = properties.filter(
          (p: any) =>
            p.name === FormSubmittedProperty.FormId ||
            p.name === FormSubmittedProperty.FormName
        );
        const cleanNode = { ...state.nodes[0], properties: cleanProperties };
        const cleanState = { ...state, nodes: [cleanNode] };
        previousFormIdRef.current = null;
        builderState = cleanState;
        qbResetStateRef.current = cleanState;
        initialNodesCache = [cleanNode];
        initialNodesCacheManuallySet = true;
        setQbKey((k) => k + 1);
      } else {
        previousFormIdRef.current = null;
      }
      return;
    }

    // Allow a reset only when:
    // - form ID changed from one value to another (previousFormIdRef !== null), OR
    // - form ID was deleted then re-set (wasFormIdEverSetRef is true but prev is null)
    // This deliberately skips the reset on the initial edit-mode render where
    // QB fires onStateChange with the saved state (prev is null, wasEverSet is false).
    if (
      String(newFormId) !== previousFormIdRef.current &&
      (previousFormIdRef.current !== null || wasFormIdEverSetRef.current)
    ) {
      const keptProperties = properties.filter(
        (p: any) =>
          p.name === FormSubmittedProperty.FormId ||
          p.name === FormSubmittedProperty.FormName
      );
      const trimmedNode = { ...state.nodes[0], properties: keptProperties };
      const trimmedState = { ...state, nodes: [trimmedNode] };
      previousFormIdRef.current = String(newFormId);
      wasFormIdEverSetRef.current = true;
      builderState = trimmedState;
      qbResetStateRef.current = trimmedState;
      initialNodesCache = [trimmedNode];
      initialNodesCacheManuallySet = true;
      setQbKey((k) => k + 1);
      return;
    }

    // If the form ID is being set for the very first time (null → value) and
    // there are condition rows that have no property name yet (added before the
    // form was selected), those rows cached empty options from the API handler.
    // Strip them and remount so the QB re-fetches with the now-known form ID.
    // In edit mode the initial render has ALL conditions already named, so
    // this block is skipped there.
    if (previousFormIdRef.current === null && !wasFormIdEverSetRef.current) {
      const hasNamelessRows = properties.some(
        (p: any) =>
          p.name !== FormSubmittedProperty.FormId &&
          p.name !== FormSubmittedProperty.FormName &&
          !p.name
      );
      if (hasNamelessRows) {
        const keptProperties = properties.filter(
          (p: any) =>
            p.name === FormSubmittedProperty.FormId ||
            p.name === FormSubmittedProperty.FormName
        );
        const trimmedNode = { ...state.nodes[0], properties: keptProperties };
        const trimmedState = { ...state, nodes: [trimmedNode] };
        previousFormIdRef.current = String(newFormId);
        wasFormIdEverSetRef.current = true;
        builderState = trimmedState;
        qbResetStateRef.current = trimmedState;
        initialNodesCache = [trimmedNode];
        initialNodesCacheManuallySet = true;
        setQbKey((k) => k + 1);
        return;
      }
    }

    wasFormIdEverSetRef.current = true;
    previousFormIdRef.current = String(newFormId);
  };

  const prepareState = (state: any) => {
    const updatedNode = state.nodes.map((node: any) => {
      const properties =
        node.properties
          ?.map((property: any) => prepareBoolean(property))
          .flat() ?? [];
      return { ...node, properties };
    });
    return { ...state, nodes: updatedNode };
  };

  const reRenderState = (nodes: any) => {
    const updatedNode = nodes.map((node: any) => {
      const properties =
        node.properties
          ?.map((property: any) => reverseParseBoolean(property))
          .flat() ?? [];
      return { ...node, properties };
    });
    return updatedNode;
  };

  const parseBuilderData = () => {
    if (props.channel !== CHANNELS.LIVECHAT && !selectedChannel?.value) {
      setErrorMsg("Please select an account");
      return;
    }
    if (selectedChannel.value === "ERROR") {
      return;
    }

    // Validate: at most one form identifier condition (formId or formName)
    const currentProperties: any[] =
      (builderState as any)?.nodes?.[0]?.properties ?? [];
    const formIdentifierCount = currentProperties.filter(
      (p: any) =>
        p.name === FormSubmittedProperty.FormId ||
        p.name === FormSubmittedProperty.FormName
    ).length;
    if (formIdentifierCount > 1) {
      setErrorMsg(
        "Only one Form Id or Form Name condition is allowed per trigger."
      );
      return;
    }

    let { isError, state } = checkForErros();
    if (isError) return;
    const { id, subHeader } = extractHeaderAndSubHeader(state);
    state = prepareState(state);
    props.onSave({
      ...state,
      header: state.nodes[0].metas[0].value,
      subHeader,
      id: props.triggerId || id,
      type: "event",
      variableList,
      channelId:
        props.channel === CHANNELS.LIVECHAT
          ? CHANNELS.LIVECHAT
          : selectedChannel.value,
    });
    builderState = {};
    initialNodesCache = [];
    initialNodesCacheManuallySet = false;
    previousFormIdRef.current = null;
    qbResetStateRef.current = undefined;
    wasFormIdEverSetRef.current = false;
    if (eventQbRef && eventQbRef.current) {
      eventQbRef.current.clearAll("EVENTS_TRIGGER");
    }
    props.onClose();
  };

  const checkForErros = () => {
    const errorStateRes = queryValidator.validate({
      include: builderState,
    });
    setErrState({ ...errorStateRes[1].include });
    setErrorMsg(errorStateRes[0]);
    return { isError: errorStateRes[0], state: errorStateRes[1].include };
  };

  // Pre-populate formFieldCache when opening in edit mode so the QB API
  // handler can resolve saved field property names on the first render.
  useEffect(() => {
    if (!props.nodes?.length) return;
    const savedProperties: any[] = (props.nodes as any)[0]?.properties ?? [];
    const formProp = savedProperties.find(
      (p: any) =>
        (p.name === FormSubmittedProperty.FormId ||
          p.name === FormSubmittedProperty.FormName) &&
        p.value
    );
    if (!formProp) return;
    const rawValue = formProp.value;
    const resolvedFormId = Array.isArray(rawValue)
      ? (rawValue[0]?.id ?? rawValue[0])
      : typeof rawValue === "object"
      ? rawValue?.id
      : rawValue;
    if (!resolvedFormId) return;

    const buildVariableList = (rawFields: any[]) => [
      FormSubmittedProperty.FormId,
      FormSubmittedProperty.FormName,
      ...rawFields.map((f: any) => f.ticket_field?.alias || f.ticket_field?.name).filter(Boolean),
    ];

    const cachedFields = formFieldCache[resolvedFormId];
    if (cachedFields) {
      variableList = buildVariableList(cachedFields);
      return;
    }

    getTicketFormFieldMap(props.storeId, resolvedFormId).then(
      (fieldsResponse) => {
        if (fieldsResponse.data) {
          const rawFields = fieldsResponse.data?.data?.fieldMap || [];
          formFieldCache[resolvedFormId] = rawFields;
          variableList = buildVariableList(rawFields);
        }
      }
    );
  }, [props.nodes, props.storeId]);

  useEffect(() => {
    if (props.channel !== CHANNELS.LIVECHAT) {
      populateChannelIdsDropdownForFlow({
        channel: props.channel,
        channels,
        setIsLoading,
        setSelectedChannel,
        setChannelIdOptions,
        currentChannelId: props.currentChannelId || channelSettingsId,
        domain,
        domainChannelMap,
        flowMetaEntry: flowMetaState[currentFlowId],
      });
    } else {
      setIsLoading(false);
    }
  }, [domainChannelMap, props.currentChannelId, channelSettingsId, domain, props.channel]);

  useEffect(() => {
    if (channelIdOptions && channelIdOptions.length === 1) {
      setSelectedChannel(channelIdOptions[0]);
    } else if (
      selectedChannel?.value &&
      selectedChannel.value !== "ERROR" &&
      channelIdOptions &&
      !channelIdOptions.some((o) => o.value === selectedChannel.value)
    ) {
      // Options recomputed (e.g. domainChannelMap loaded) and the current
      // selection is no longer valid for this flow's domain — clear it so a
      // stale cross-store account doesn't linger.
      setSelectedChannel({});
    }

    if (props.channel !== CHANNELS.LIVECHAT) {
      if (selectedChannel.value === "ERROR" && props?.currentChannelId) {
        setErrorMsg("No account is connected to this trigger. Please check");
      } else {
        setErrorMsg("");
      }
    }
  }, [channelIdOptions, selectedChannel]);

  const getDropDownIcon = (channel: string) => {
    if (channel === "whatsapp") {
      return <WhatsappIcon />;
    } else if (channel === "instagram") {
      return <InstagramIcon />;
    } else {
      return <EmailIcon />;
    }
  };

  const InstaDropdown = () => {
    return (
      <InstaDropdownContainer>
        <DropdownPopover
          width="200px"
          disabled={isLoading}
          onSelect={(c: any) => {
            setSelectedChannel(c);
            const modified = channelIdOptions.map((d) => {
              if (d.value === c.value) {
                return {
                  label: d.label,
                  value: d.value,
                  selected: true,
                };
              } else {
                return {
                  label: d.label,
                  value: d.value,
                  selected: false,
                };
              }
            });
            setChannelIdOptions(modified);
          }}
          options={channelIdOptions}
        >
          <InstaDropdownWrapper>
            {getDropDownIcon(props.channel)}
            <TitleSmall className="dropdown-text" style={{ fontWeight: 400 }}>
              {selectedChannel.label || "Select an account"}
            </TitleSmall>
            <ChevronDownIcon color={COLORS.content.primary} />
          </InstaDropdownWrapper>
        </DropdownPopover>
      </InstaDropdownContainer>
    );
  };

  return (
    <>
      <StyledModal
        zIndex={200}
        centralContainerStyles={{ width: "1032px", height: "560px" }}
        onClose={() => {
          handleModalClose();
        }}
        open={props.visible}
      >
        <Container>
          <div style={{ display: "flex" }}>
            <Header>
              <TitleMedium>{`Create trigger based on events`}</TitleMedium>
            </Header>
            {props.channel !== CHANNELS.LIVECHAT && (
              <div
                style={{
                  marginTop: 15,
                  paddingRight: 15,
                  borderBottom: `1px solid ${COLORS.stroke.primary}`,
                }}
              >
                <InstaDropdown />
              </div>
            )}
          </div>

          <Body>
            <BuilderContainer>
              {props.visible && (
              <QueryBuilder
                key={qbKey}
                ref={eventQbRef}
                mode={props.mode}
                state={
                  qbResetStateRef.current
                    ? qbResetStateRef.current
                    : props.nodes && props.nodes.length
                    ? { nodes: reRenderState(props.nodes) }
                    : undefined
                }
                selectorKey="eventTrigger"
                addNodeBtnText="Add Trigger"
                queryPreText="Whenever user performs"
                infinite={false}
                onStateChange={handleStateChange}
                errorState={errorState}
                typesAPIHandlers={{
                  EVENTS_TRIGGER: async (
                    payload: BaseQueryBuilderPayload<
                      | { triggerName: string }
                      | {
                          triggerName: string;
                          propertyName: string;
                          searchKey?: string;
                        }
                      | undefined
                    >
                  ) => {
                    return await eventsTriggerApiHandler(
                      payload,
                      props.storeId,
                      props.channel,
                      props.category,
                      isVersionApplicabletoPlan("AUTO_CLOSED_IN_JOURNEYS")
                    );
                  },
                }}
              />
              )}
            </BuilderContainer>
          </Body>

          <Footer>
            {errorMsg ? (
              <Tag tagText={errorMsg} type="negative" LeadingIcon={WarnIcon} />
            ) : props.footerCopy ? (
              <BodySecondary style={{ color: BASE_COLORS.grayscale["700"] }}>
                {props.footerCopy}
              </BodySecondary>
            ) : (
              <div />
            )}
            <div style={{ display: "flex", gap: 8 }}>
              {props.onBack && (
                <Button
                  buttonText="Back"
                  size={"medium"}
                  buttonType="secondary"
                  onClick={props.onBack}
                />
              )}
              <Button
                buttonText={props.ctaText ?? "Create"}
                size={"medium"}
                onClick={() => {
                  parseBuilderData();
                }}
              />
            </div>
          </Footer>
        </Container>
      </StyledModal>
    </>
  );
};

export default EventsBuilder;
